import React, { useEffect, useState } from 'react';
import { theme, card, btnPrimary, btnSecondary } from '../theme';

// Client-admin "Licensing & seats" panel on the Account screen. Shows
// purchased vs used seats for a product and lets an owner/admin assign or
// remove team members. Calls DropPin's /api/account-seats (which proxies to
// InSite-ID with the cap enforced). v0.47.0.
export default function SeatLicensingPanel({ productCode = 'droppin', productLabel = 'DropPin' }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const load = () => {
    setErr(null);
    fetch('/api/account-seats?productCode=' + encodeURIComponent(productCode), { credentials: 'include' })
      .then(r => (r.ok ? r.json() : r.json().then(j => Promise.reject(new Error(j.error || ('HTTP ' + r.status))))))
      .then(setData)
      .catch(e => setErr(e.message));
  };
  useEffect(load, [productCode]);

  const isAdmin = !!data && ['owner', 'admin'].includes(String(data.role || '').toLowerCase());
  const seatUserIds = new Set((data && data.seats ? data.seats : []).map(s => s.UserId));
  const purchased = (data && data.seatsPurchased) || 0;
  const used = (data && data.seatsUsed) || 0;
  const free = purchased - used;

  const toggle = async (m) => {
    const has = seatUserIds.has(m.UserId);
    setBusy(m.UserId); setErr(null);
    try {
      const r = await fetch('/api/account-seats', {
        method: has ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ productCode, userId: m.UserId }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + r.status)); }
      load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const invite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true); setErr(null);
    try {
      const r = await fetch('/api/account-seats', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ invite: true, email }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || ('HTTP ' + r.status)); }
      setInviteEmail(''); load();
    } catch (e) { setErr(e.message); }
    finally { setInviting(false); }
  };

  return (
    <div style={{ ...card, padding: 20, marginTop: 16 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Licensing &amp; seats</h2>
      <div style={{ fontSize: 13, color: theme.textLight, marginBottom: 14 }}>
        Assign your team to {productLabel} seats.{isAdmin ? '' : ' Only an owner or admin can change these.'}
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{err}</div>}
      {!data ? (
        <div style={{ color: theme.textLight, fontSize: 13 }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
            <Stat label="Purchased" value={purchased} />
            <Stat label="In use" value={used} />
            <Stat label="Free" value={free} danger={free <= 0} />
          </div>
          <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden' }}>
            {(data.members || []).length === 0 && (
              <div style={{ padding: 14, color: theme.textLight, fontSize: 13 }}>No team members yet.</div>
            )}
            {(data.members || []).map(m => {
              const has = seatUserIds.has(m.UserId);
              const name = [m.FirstName, m.LastName].filter(Boolean).join(' ') || m.Email;
              return (
                <div key={m.UserId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderTop: `1px solid ${theme.border}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{name}</div>
                    <div style={{ fontSize: 11, color: theme.textLight }}>{m.Email}{m.Role ? ' · ' + m.Role : ''}</div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: has ? '#dcfce7' : '#f1f5f9', color: has ? '#166534' : theme.textLight }}>
                    {has ? 'Licensed' : 'No seat'}
                  </span>
                  {isAdmin && (
                    <button type="button" onClick={() => toggle(m)} disabled={busy === m.UserId || (!has && free <= 0)}
                      style={{ ...(has ? btnSecondary : btnPrimary), padding: '6px 12px', fontSize: 12, opacity: (busy === m.UserId || (!has && free <= 0)) ? 0.5 : 1 }}>
                      {busy === m.UserId ? '…' : has ? 'Remove' : 'Assign'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {isAdmin && (
            <div style={{ marginTop: 14, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                placeholder="Invite a teammate by email…" type="email"
                style={{ flex: 1, minWidth: 200, padding: '8px 10px', border: `1px solid ${theme.border}`, borderRadius: 6, fontSize: 13, background: theme.surface, color: theme.text }} />
              <button type="button" onClick={invite} disabled={inviting || !inviteEmail.trim()}
                style={{ ...btnPrimary, padding: '8px 14px', fontSize: 13, opacity: (inviting || !inviteEmail.trim()) ? 0.5 : 1 }}>
                {inviting ? 'Inviting…' : 'Invite'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, danger }) {
  return (
    <div>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: theme.textLight }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: danger ? '#dc2626' : theme.text }}>{value}</div>
    </div>
  );
}
